import { type FrontRoyalStore } from 'FrontRoyalStore';
import StorableImageError from './StorableImageError';

// Do not narrow this with `anyOf`. Reading every key is much faster on iOS.
export async function loadStoredImageUrls(frontRoyalStore: FrontRoyalStore) {
    const urls = await frontRoyalStore.retryOnHandledError(db => db.images.toCollection().primaryKeys());
    return new Set(urls);
}

export default class StoredImagesRepo {
    urls: string[];
    frontRoyalStore: FrontRoyalStore;
    urlsToRetry: string[] = [];
    // Shared across repos so that a url used by two lessons is only fetched once.
    claimedUrls: Set<string>;

    constructor(urls: string[], frontRoyalStore: FrontRoyalStore, claimedUrls: Set<string>) {
        this.urls = urls;
        this.frontRoyalStore = frontRoyalStore;
        this.claimedUrls = claimedUrls;
    }

    async fetchAndStoreImages() {
        await this.fetchAndStoreEach(this.urls, 'retry');

        // The retry bypasses the http cache.
        // We added that after we hit a 502 cached for a year. See https://trello.com/c/QHzY0hMK.
        await this.fetchAndStoreEach(this.urlsToRetry, 'throw', 'reload');
    }

    // One image at a time, each written before the next is fetched. Nothing accumulates, so no
    // pool or batch bounds the buffers. Streams and lessons already run one at a time, which makes
    // the whole pass serial.
    async fetchAndStoreEach(urls: string[], onError?: string, cache?: RequestCache) {
        for (let i = 0; i < urls.length; i += 1) {
            // oxlint-disable-next-line no-await-in-loop
            await this.fetchAndStoreImage(urls[i], onError, cache);
        }
    }

    async fetchAndStoreImage(url: string, onError = 'throw', cache: RequestCache = 'default') {
        if (this.claimedUrls.has(url)) return;

        // Claim before the await, not after the put. A url claimed only after its put lets a
        // second lesson start a duplicate fetch while the first one is still in flight.
        this.claimedUrls.add(url);

        const genericFailureMsg = `Failed to fetch image ${url}`;
        let data: ArrayBuffer;

        try {
            const response = await fetch(url, { cache });
            if (!response.ok) throw new Error(genericFailureMsg);
            data = await response.arrayBuffer();
        } catch (error) {
            this.claimedUrls.delete(url);

            if (onError === 'retry') {
                this.urlsToRetry.push(url);
                return;
            }
            throw new StorableImageError((error instanceof Error && error.message) || genericFailureMsg, {
                cause: error,
            });
        }

        // The put sits outside the catch because a storage error is not a fetch error and must not
        // turn into a fetch retry.
        await this.frontRoyalStore.retryRequestOnHandledError(
            'storeImages',
            [{ url, data }],
            // See comment near similar line in getStreamAndEnsureStored
            { errorHandlingPolicies: { throwOnStorageQuotaExceededError: true } },
        );
    }
}
